import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";

const StyledCard = styled(motion.div)(({ theme }) => ({
  background: "#FFFFFF",
  borderRadius: "16px",
  padding: "32px 24px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s ease",
  cursor: "pointer",
  position: "relative",
  minHeight: "300px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
  },
  [theme.breakpoints.down("md")]: {
    minHeight: "280px",
    padding: "28px 20px",
  },
}));

const IconCircle = styled(Box)(({ iconColor }) => ({
  width: "72px",
  height: "72px",
  borderRadius: "50%",
  background: iconColor || "#FF8C42",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "20px",
  boxShadow: `0 8px 24px ${iconColor}40`,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.1)",
  },
}));

const StepBadge = styled(Box)(({ theme }) => ({
  background: "#FFF4E6",
  color: "#8B6914",
  padding: "6px 16px",
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: 600,
  marginBottom: "16px",
  display: "inline-block",
  fontFamily: "'Poppins', sans-serif",
}));

const ProcessCard = ({ number, icon, title, description, color, delay = 0 }) => {
  const IconComponent = LucideIcons[icon];

  return (
    <StyledCard
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay }}
    >
      <Stack spacing={2} alignItems="center">
        <StepBadge>Step {number}</StepBadge>
        
        <IconCircle iconColor={color}>
          {IconComponent && (
            <Box
              component={motion.div}
              animate={{
                y: [0, -8, 0],
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: delay * 0.2,
              }}
            >
              <IconComponent size={36} color="#FFFFFF" strokeWidth={2.5} />
            </Box>
          )}
        </IconCircle>
        
        <Typography
          sx={{
            color: "#282D4D",
            fontWeight: 700,
            fontSize: "18px",
            mt: 1,
            fontFamily: "'Poppins', sans-serif",
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        
        <Typography
          sx={{
            color: "#7F7F7F",
            fontSize: "14px",
            lineHeight: 1.6,
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 400,
          }}
        >
          {description}
        </Typography>
      </Stack>
    </StyledCard>
  );
};

export default ProcessCard;